import RefreshIcon from "@mui/icons-material/Refresh";
import {
	Box,
	Grid,
	IconButton,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { ExecuteCodeListImportCommandResult } from "Infrastructure/Api/Api";
import { AccessRightCodes } from "Infrastructure/Models/AccessRightCodes";
import { useEffect, useRef, useState } from "react";
import { useExecuteImportMutation } from "../../Api/Mutations/useExecuteImportMutation";
import { useImportEnvironmentsQuery } from "../../Api/Queries/useImportEnvironmentsQuery";
import {
	IMPORT_JOBS_QUERY_KEY,
	useImportJobsQuery,
	useRunningImportJobQuery,
} from "../../Api/Queries/useImportJobsQuery";
import { ImportForm } from "./ImportForm";
import { ImportHistory } from "./ImportHistory";
import { ImportProgress } from "./ImportProgress";
import { RunningImportBanner } from "./RunningImportBanner";

export const ReleaseTab: React.FunctionComponent = () => {
	const [importResult, setImportResult] =
		useState<ExecuteCodeListImportCommandResult | null>(null);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);

	const queryClient = useQueryClient();
	const wasRunningRef = useRef(false);

	const offset = page * pageSize;

	const { data: userInfoData } = useUserInfoQuery();
	const userAccessRights =
		userInfoData?.status === 200
			? (userInfoData.data.accessRightCodes ?? [])
			: [];
	const canImport = userAccessRights.includes(
		AccessRightCodes.CodelistManagementImport
	);

	const { data: environmentsData, isLoading: environmentsLoading } =
		useImportEnvironmentsQuery();
	const { data: jobsData, isLoading: jobsLoading } = useImportJobsQuery(
		null,
		null,
		offset,
		pageSize
	);
	const { runningJob, isRunning } = useRunningImportJobQuery();

	const executeMutation = useExecuteImportMutation();

	const environments =
		environmentsData?.status === 200 ? environmentsData.data : [];
	const jobsResult = jobsData?.status === 200 ? jobsData.data : null;
	const jobs = jobsResult?.items ?? [];
	const totalCount = jobsResult?.totalCount ?? 0;

	useEffect(() => {
		if (wasRunningRef.current && !isRunning) {
			queryClient.invalidateQueries({ queryKey: [IMPORT_JOBS_QUERY_KEY] });
		}
		wasRunningRef.current = isRunning;
	}, [isRunning, queryClient]);

	const handleExecuteImport = (
		environmentName: string,
		localization: string
	) => {
		setImportResult(null);
		executeMutation.mutate(
			{
				environmentName,
				localization,
			},
			{
				onSuccess: (response) => {
					if (response.status === 200) {
						setImportResult(response.data);
					}
				},
			}
		);
	};

	const handleRefresh = () => {
		queryClient.invalidateQueries({ queryKey: [IMPORT_JOBS_QUERY_KEY] });
	};

	const showRunningBanner = isRunning || executeMutation.isPending;
	const isBlockedByRunningJob = isRunning && !executeMutation.isPending;
	const isBlocked = isBlockedByRunningJob || !canImport;

	return (
		<Box sx={{ p: 2 }}>
			<Grid container spacing={3}>
				<Grid item xs={12} md={4}>
					<Stack spacing={3}>
						<ImportForm
							environments={environments}
							isLoading={environmentsLoading}
							isExecuting={executeMutation.isPending}
							isBlocked={isBlocked}
							onExecute={handleExecuteImport}
							noPermission={!canImport}
						/>

						{showRunningBanner && (
							<RunningImportBanner
								job={runningJob}
								isPending={executeMutation.isPending && !runningJob}
							/>
						)}

						<ImportProgress result={importResult} />
					</Stack>
				</Grid>

				<Grid item xs={12} md={8}>
					<ImportHistory
						jobs={jobs}
						totalCount={totalCount}
						page={page}
						pageSize={pageSize}
						isLoading={jobsLoading}
						onPageChange={setPage}
						onPageSizeChange={(newPageSize) => {
							setPageSize(newPageSize);
							setPage(0);
						}}
					/>
				</Grid>
			</Grid>
		</Box>
	);
};
